import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ShoppingBag, Plane, Hotel, Hospital, Briefcase, ParkingSquare, Car, KeyRound,
  Check, ArrowRight,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { submitBusinessEnquiry } from "@/lib/api";

const ICONS = [ShoppingBag, Plane, Hotel, Hospital, Briefcase, ParkingSquare, Car, KeyRound];

export default function BusinessSolutions() {
  const { t, isRTL } = useI18n();
  const solutions = t("business.solutions");
  const benefits = t("business.benefits");
  const f = t("business.form");
  const emirates = t("early.form.emirates");

  const [form, setForm] = useState({
    full_name: "", organisation: "", job_title: "", email: "",
    mobile: "", organisation_type: "", emirate: "",
    facilities_count: "", pilot_location: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.organisation || !form.email || !form.mobile
        || !form.organisation_type || !form.emirate) {
      toast.error(t("common.required"));
      return;
    }
    setSubmitting(true);
    try {
      await submitBusinessEnquiry(form);
      toast.success(f.success, { id: "business-enquiry-success" });
      setForm({
        full_name: "", organisation: "", job_title: "", email: "",
        mobile: "", organisation_type: "", emirate: "",
        facilities_count: "", pilot_location: "", message: "",
      });
    } catch {
      toast.error(f.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="partners" className="section bg-white" data-testid="section-business">
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="eyebrow">{t("business.eyebrow")}</div>
          <h2 className="h2 mt-4">{t("business.title")}</h2>
          <p className="lede mt-5">{t("business.lede")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {solutions.map((s, i) => {
            const Icon = ICONS[i] || ShoppingBag;
            return (
              <div key={i} className="card-soft p-6 hover:-translate-y-1 hover:shadow-lift transition-transform duration-200" data-testid={`business-solution-${i}`}>
                <div className="h-10 w-10 rounded-xl bg-brand-greenSoft text-brand-green flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-5 text-base font-bold text-brand-charcoal">{s.t}</div>
                <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/70">{s.d}</p>
              </div>
            );
          })}
        </div>

        {/* Benefits + Form grid */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 rounded-3xl bg-brand-charcoal text-white p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -bottom-10 -end-10 h-40 w-40 rounded-full bg-brand-gold/20 blur-2xl" />
            <div className="eyebrow text-brand-gold" style={{ color: "#C8A951" }}>
              {t("business.benefits_title")}
            </div>
            <h3 className="mt-4 text-2xl md:text-3xl font-extrabold leading-tight">
              {t("business.title")}
            </h3>
            <ul className="mt-6 grid grid-cols-1 gap-2.5">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/85">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-brand-gold text-brand-charcoal flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => document.getElementById("business-form")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-charcoal px-6 py-3 text-sm font-bold transition-colors duration-200 hover:bg-white"
              data-testid="business-cta-pilot"
            >
              {t("business.cta")}
              <ArrowRight className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} />
            </button>
          </div>

          <motion.form
            id="business-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3 rounded-3xl border border-brand-line bg-white p-8 md:p-10"
            data-testid="business-form"
          >
            <div className="text-brand-charcoal">
              <div className="text-xl font-bold">{f.title}</div>
              <div className="text-sm text-brand-charcoal/60 mt-1">{f.subtitle}</div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={f.full_name} value={form.full_name} onChange={update("full_name")} testid="biz-full-name" required />
              <Field label={f.organisation} value={form.organisation} onChange={update("organisation")} testid="biz-organisation" required />
              <Field label={f.job_title} value={form.job_title} onChange={update("job_title")} testid="biz-job-title" />
              <Field label={f.email} type="email" value={form.email} onChange={update("email")} testid="biz-email" required />
              <Field label={f.mobile} value={form.mobile} onChange={update("mobile")} testid="biz-mobile" required />
              <SelectField
                label={f.organisation_type}
                value={form.organisation_type}
                onChange={update("organisation_type")}
                options={f.organisation_types}
                placeholder={f.organisation_type_ph}
                testid="biz-org-type"
                required
              />
              <SelectField
                label={f.emirate}
                value={form.emirate}
                onChange={update("emirate")}
                options={emirates}
                placeholder={t("early.form.emirate_ph")}
                testid="biz-emirate"
                required
              />
              <Field label={f.facilities} value={form.facilities_count} onChange={update("facilities_count")} testid="biz-facilities" />
              <div className="md:col-span-2">
                <Field label={f.pilot_location} value={form.pilot_location} onChange={update("pilot_location")} testid="biz-pilot-location" />
              </div>
              <div className="md:col-span-2">
                <TextAreaField label={f.message} value={form.message} onChange={update("message")} testid="biz-message" />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 btn-primary w-full md:w-auto"
              data-testid="biz-submit"
            >
              {submitting ? t("common.submitting") : f.submit}
              {!submitting && <ArrowRight className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} />}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal placeholder:text-brand-charcoal/40 outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150"
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">{label}</span>
      <textarea
        rows={4}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal placeholder:text-brand-charcoal/40 outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150 resize-none"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, placeholder, testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <select
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}
